import React, { useCallback, useState } from 'react';
import { View, Text, FlatList, StyleSheet, ActivityIndicator, Alert } from 'react-native';
import { useFocusEffect } from 'expo-router';
import { useTranslation } from 'react-i18next';
import '../../i18n';
import { useAuth } from '../../features/auth/useAuth';
import { useDb } from '../../db/client';
import { listSupplies, createSupply, updateSupply, deleteSupply } from '../../features/supplies/supplyRepository';
import { parseLocaleNumber } from '../shared/parseLocaleNumber';
import { formatLocaleNumber } from '../shared/formatLocaleNumber';
import type { Supply } from '../../db/schema';
import { colors, fonts, spacing } from '../theme';
import { Heading } from '../shared/Typography';
import { TextField } from '../shared/TextField';
import { Button } from '../shared/Button';
import { Chip } from '../shared/Chip';

// Lista aberta (não enum, ver spec S02) — só sugestões de atalho, o campo
// de tipo continua um TextInput livre. `value` é o dado gravado em
// `supplies.type` (estável, não deve mudar); `labelKey` é só o rótulo
// visível do chip — regra invíolável #1 (toda string de UI via i18n), o
// `value` em si não é texto de UI, é dado de domínio.
const TYPE_SUGGESTIONS: Array<{ value: string; labelKey: string }> = [
  { value: 'fio/linha', labelKey: 'supply.catalog.typeSuggestions.fioLinha' },
  { value: 'agulha', labelKey: 'supply.catalog.typeSuggestions.agulha' },
  { value: 'enchimento', labelKey: 'supply.catalog.typeSuggestions.enchimento' },
  { value: 'aviamento', labelKey: 'supply.catalog.typeSuggestions.aviamento' },
  { value: 'embalagem', labelKey: 'supply.catalog.typeSuggestions.embalagem' },
  { value: 'outro', labelKey: 'supply.catalog.typeSuggestions.outro' },
];

interface FormState {
  type: string;
  name: string;
  unit: string;
  unitPriceText: string;
}

const EMPTY_FORM: FormState = { type: '', name: '', unit: '', unitPriceText: '' };

export function CatalogScreen() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { db, ready } = useDb();

  const [supplies, setSupplies] = useState<Supply[]>([]);
  const [loading, setLoading] = useState(true);

  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState<FormState>(EMPTY_FORM);
  const [submittingCreate, setSubmittingCreate] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<FormState>(EMPTY_FORM);
  const [submittingEdit, setSubmittingEdit] = useState(false);

  const load = useCallback(async () => {
    if (!ready || !db || !user) return;
    setLoading(true);
    try {
      const result = await listSupplies(db, user.id);
      setSupplies(result);
    } catch (error) {
      console.error('CatalogScreen: falha ao carregar catálogo', error);
    } finally {
      setLoading(false);
    }
  }, [ready, db, user]);

  // useFocusEffect: um insumo pode ser criado inline a partir da tela de
  // projeto (fora desta aba) — recarregar a cada foco garante que ele
  // apareça aqui assim que a usuária volta para a aba Catálogo, mesmo que
  // a tela já estivesse montada em segundo plano (mesmo raciocínio de
  // ProjectsListScreen).
  useFocusEffect(
    useCallback(() => {
      load();
    }, [load])
  );

  const validateForm = (form: FormState): number | null => {
    const unitPrice = parseLocaleNumber(form.unitPriceText);
    if (!form.type.trim() || !form.name.trim() || !form.unit.trim() || unitPrice === null) {
      Alert.alert(t('supply.catalog.errorTitle'), t('supply.catalog.requiredFieldsMessage'));
      return null;
    }
    return unitPrice;
  };

  const handleCreate = async () => {
    if (!db || !user) return;
    const unitPrice = validateForm(createForm);
    if (unitPrice === null) return;

    setSubmittingCreate(true);
    try {
      const supply = await createSupply(db, {
        userId: user.id,
        type: createForm.type.trim(),
        name: createForm.name.trim(),
        unit: createForm.unit.trim(),
        unitPrice,
      });
      setSupplies((current) => [...current, supply]);
      setCreateForm(EMPTY_FORM);
      setCreating(false);
    } catch (error) {
      Alert.alert(t('supply.catalog.errorTitle'), (error as Error).message);
    } finally {
      setSubmittingCreate(false);
    }
  };

  const startEdit = (supply: Supply) => {
    setEditingId(supply.id);
    setEditForm({ type: supply.type, name: supply.name, unit: supply.unit, unitPriceText: String(supply.unitPrice) });
  };

  const handleSaveEdit = async (original: Supply) => {
    if (!db) return;
    const unitPrice = validateForm(editForm);
    if (unitPrice === null) return;

    const priceChanged = unitPrice !== original.unitPrice;

    setSubmittingEdit(true);
    try {
      const updated = await updateSupply(db, original.id, {
        type: editForm.type.trim(),
        name: editForm.name.trim(),
        unit: editForm.unit.trim(),
        unitPrice,
      });
      setSupplies((current) => current.map((s) => (s.id === updated.id ? updated : s)));
      setEditingId(null);

      // O snapshot de vínculos já existentes NÃO muda (ver
      // src/features/projects/linkSupply.ts) — este aviso é só
      // informativo, a spec S02 não exige uma ação de "atualizar" aqui.
      if (priceChanged) {
        Alert.alert(t('supply.catalog.priceUpdateNoticeTitle'), t('supply.catalog.priceUpdateNoticeMessage'));
      }
    } catch (error) {
      Alert.alert(t('supply.catalog.errorTitle'), (error as Error).message);
    } finally {
      setSubmittingEdit(false);
    }
  };

  const handleDelete = (supply: Supply) => {
    if (!db) return;

    Alert.alert(t('supply.catalog.deleteConfirmTitle'), t('supply.catalog.deleteConfirmMessage'), [
      { text: t('supply.catalog.deleteConfirmCancel'), style: 'cancel' },
      {
        text: t('supply.catalog.deleteConfirmConfirm'),
        style: 'destructive',
        onPress: async () => {
          try {
            await deleteSupply(db, supply.id);
            setSupplies((current) => current.filter((s) => s.id !== supply.id));
          } catch (error) {
            Alert.alert(t('supply.catalog.errorTitle'), (error as Error).message);
          }
        },
      },
    ]);
  };

  return (
    <View style={styles.container}>
      <Heading style={styles.title}>{t('tabs.catalog.title')}</Heading>

      {creating ? (
        <View style={styles.form}>
          <View style={styles.suggestionRow}>
            {TYPE_SUGGESTIONS.map((suggestion) => (
              <Chip
                key={suggestion.value}
                label={t(suggestion.labelKey)}
                active={createForm.type === suggestion.value}
                onPress={() => setCreateForm((f) => ({ ...f, type: suggestion.value }))}
              />
            ))}
          </View>
          <TextField
            placeholder={t('supply.catalog.typePlaceholder')}
            value={createForm.type}
            onChangeText={(type) => setCreateForm((f) => ({ ...f, type }))}
          />
          <TextField
            placeholder={t('supply.catalog.namePlaceholder')}
            value={createForm.name}
            onChangeText={(name) => setCreateForm((f) => ({ ...f, name }))}
          />
          <TextField
            placeholder={t('supply.catalog.unitPlaceholder')}
            value={createForm.unit}
            onChangeText={(unit) => setCreateForm((f) => ({ ...f, unit }))}
          />
          <TextField
            placeholder={t('supply.catalog.unitPricePlaceholder')}
            value={createForm.unitPriceText}
            onChangeText={(unitPriceText) => setCreateForm((f) => ({ ...f, unitPriceText }))}
            keyboardType="decimal-pad"
          />
          {submittingCreate ? (
            <ActivityIndicator color={colors.sageDeep} />
          ) : (
            <>
              <Button title={t('supply.catalog.save')} onPress={handleCreate} />
              <Button
                title={t('supply.catalog.cancel')}
                variant="ghost"
                onPress={() => {
                  setCreating(false);
                  setCreateForm(EMPTY_FORM);
                }}
              />
            </>
          )}
        </View>
      ) : (
        <Button title={t('supply.catalog.newButton')} onPress={() => setCreating(true)} />
      )}

      {!ready || loading ? (
        <Text style={styles.muted}>{t('supply.catalog.loading')}</Text>
      ) : (
        <FlatList
          style={styles.list}
          data={supplies}
          keyExtractor={(item) => item.id}
          ListEmptyComponent={<Text style={styles.muted}>{t('supply.catalog.empty')}</Text>}
          renderItem={({ item }) =>
            editingId === item.id ? (
              <View style={styles.form}>
                <TextField value={editForm.type} onChangeText={(type) => setEditForm((f) => ({ ...f, type }))} />
                <TextField value={editForm.name} onChangeText={(name) => setEditForm((f) => ({ ...f, name }))} />
                <TextField value={editForm.unit} onChangeText={(unit) => setEditForm((f) => ({ ...f, unit }))} />
                <TextField
                  value={editForm.unitPriceText}
                  onChangeText={(unitPriceText) => setEditForm((f) => ({ ...f, unitPriceText }))}
                  keyboardType="decimal-pad"
                />
                {submittingEdit ? (
                  <ActivityIndicator color={colors.sageDeep} />
                ) : (
                  <>
                    <Button title={t('supply.catalog.save')} onPress={() => handleSaveEdit(item)} />
                    <Button title={t('supply.catalog.cancel')} variant="ghost" onPress={() => setEditingId(null)} />
                  </>
                )}
              </View>
            ) : (
              <View style={styles.row}>
                <Text style={styles.rowName}>{item.name}</Text>
                <Text style={styles.rowMeta}>
                  {item.type} · {item.unit} · {t('common.currencyAmount', { value: formatLocaleNumber(item.unitPrice) })}
                </Text>
                <View style={styles.rowActions}>
                  <Button title={t('supply.catalog.edit')} variant="line" onPress={() => startEdit(item)} />
                  <Button title={t('supply.catalog.delete')} variant="danger" onPress={() => handleDelete(item)} />
                </View>
              </View>
            )
          }
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: spacing.md, backgroundColor: colors.sand },
  title: { textAlign: 'left', marginBottom: spacing.md },
  form: { marginBottom: spacing.md },
  suggestionRow: { flexDirection: 'row', flexWrap: 'wrap' },
  list: { flex: 1, marginTop: spacing.md },
  row: { paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.line },
  rowName: { fontFamily: fonts.bodyBold, fontSize: 16, color: colors.ink },
  rowMeta: { fontFamily: fonts.body, fontSize: 13, color: colors.inkSoft, marginBottom: spacing.xs },
  rowActions: { flexDirection: 'row', gap: spacing.sm },
  muted: { fontFamily: fonts.body, color: colors.thread, marginTop: spacing.md, textAlign: 'center' },
});
